import * as THREE from 'three';

export interface PipRect {
  /** Fracciones del lienzo [0..1] con origen abajo-izquierda (convención GL). */
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Rectángulos compartidos con el HUD (que dibuja los marcos con CSS). */
export const PIP_RECTS = {
  reverse: { x: 0.31, y: 0.03, w: 0.38, h: 0.3 },
  left: { x: 0.02, y: 0.62, w: 0.3, h: 0.26 },
  right: { x: 0.68, y: 0.62, w: 0.3, h: 0.26 },
  rear: { x: 0.33, y: 0.66, w: 0.34, h: 0.26 },
} as const satisfies Record<string, PipRect>;

/**
 * Vista Picture-in-Picture con volteo horizontal (como un espejo o una
 * cámara de marcha atrás reales): render a textura + quad con UV invertidas.
 */
export class PipView {
  readonly camera: THREE.PerspectiveCamera;
  private rt: THREE.WebGLRenderTarget;
  private quadScene = new THREE.Scene();
  private quadCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private material: THREE.MeshBasicMaterial;

  constructor(fovDeg: number, aspect: number) {
    this.camera = new THREE.PerspectiveCamera(fovDeg, aspect, 0.15, 320);
    this.rt = new THREE.WebGLRenderTarget(512, Math.round(512 / aspect));
    const geo = new THREE.PlaneGeometry(2, 2);
    const uv = geo.getAttribute('uv') as THREE.BufferAttribute;
    for (let i = 0; i < uv.count; i++) uv.setX(i, 1 - uv.getX(i)); // espejo
    uv.needsUpdate = true;
    this.material = new THREE.MeshBasicMaterial({ map: this.rt.texture, depthTest: false });
    this.quadScene.add(new THREE.Mesh(geo, this.material));
  }

  render(renderer: THREE.WebGLRenderer, scene: THREE.Scene, rect: PipRect): void {
    const size = renderer.getSize(new THREE.Vector2());
    const prevAutoClear = renderer.autoClear;
    renderer.setRenderTarget(this.rt);
    renderer.autoClear = true;
    renderer.render(scene, this.camera);
    renderer.setRenderTarget(null);
    renderer.autoClear = false;
    const px = Math.round(rect.x * size.x);
    const py = Math.round(rect.y * size.y);
    const pw = Math.round(rect.w * size.x);
    const ph = Math.round(rect.h * size.y);
    renderer.setViewport(px, py, pw, ph);
    renderer.setScissor(px, py, pw, ph);
    renderer.setScissorTest(true);
    renderer.render(this.quadScene, this.quadCam);
    renderer.setScissorTest(false);
    renderer.setViewport(0, 0, size.x, size.y);
    renderer.autoClear = prevAutoClear;
  }

  dispose(): void {
    this.rt.dispose();
    this.material.dispose();
  }
}
